import React from 'react';
import type { AnnouncementLayout, AnnouncementPosition } from '@chirpback/protocol';
import { backdropAnim, drawerAnim, modalAnim } from '../motion/keyframes.js';

interface Props {
  layout?: AnnouncementLayout;
  position?: AnnouncementPosition;
}

// Layout-aware announcement skeleton. The real shells (ModalShell, DrawerShell,
// BannerShell, ToastShell) animate in via CSS keyframes from main.tsx; we mirror
// their shape so the swap is visually continuous.
export const AnnouncementSkeleton: React.FC<Props> = ({ layout = 'modal', position }) => {
  if (layout === 'banner') return <BannerSkeleton position={position} />;
  if (layout === 'toast') return <ToastSkeleton position={position} />;
  if (layout === 'drawer') return <DrawerSkeleton position={position} />;
  if (layout === 'inline') return <InlineSkeleton />;
  return <ModalSkeleton />;
};

const ModalSkeleton: React.FC = () => (
  <div role="dialog" aria-busy="true" style={modalBackdrop}>
    <div style={modalCard}>
      <div style={{ ...bar, width: '50%', height: 22, marginBottom: 12 }} />
      <div style={{ ...bar, width: '80%', height: 14, marginBottom: 8 }} />
      <div style={{ ...bar, width: '70%', height: 14, marginBottom: 24 }} />
      <div style={{ ...bar, width: '100%', height: 160, marginBottom: 24, borderRadius: 12 }} />
      <div style={{ ...bar, width: 120, height: 36 }} />
    </div>
  </div>
);

const DrawerSkeleton: React.FC<{ position?: AnnouncementPosition }> = ({ position }) => {
  const right = position !== 'left';
  return (
    <div style={drawerBackdrop}>
      <div
        style={{
          ...drawerPanel,
          [right ? 'right' : 'left']: 0,
          animation: drawerAnim(right ? 'right' : 'left'),
        }}
      >
        <div style={{ ...bar, width: '60%', height: 20, marginBottom: 16 }} />
        <div style={{ ...bar, width: '100%', height: 14, marginBottom: 8 }} />
        <div style={{ ...bar, width: '90%', height: 14, marginBottom: 24 }} />
        <div style={{ ...bar, width: '100%', height: 140, marginBottom: 16, borderRadius: 10 }} />
        <div style={{ ...bar, width: '100%', height: 14, marginBottom: 8 }} />
        <div style={{ ...bar, width: '85%', height: 14 }} />
      </div>
    </div>
  );
};

const BannerSkeleton: React.FC<{ position?: AnnouncementPosition }> = ({ position }) => {
  const top = position !== 'bottom';
  return (
    <div style={{ ...bannerBase, [top ? 'top' : 'bottom']: 0 }}>
      <div style={{ ...bar, width: 220, height: 14 }} />
      <div style={{ ...bar, width: 80, height: 28, borderRadius: 14 }} />
    </div>
  );
};

const ToastSkeleton: React.FC<{ position?: AnnouncementPosition }> = ({ position }) => {
  const [vert, horiz] = (position ?? 'bottom-right').split('-') as ['top' | 'bottom', 'left' | 'right'];
  return (
    <div
      style={{
        ...toastBase,
        [vert]: 16,
        [horiz]: 16,
      }}
    >
      <div style={{ ...bar, width: '70%', height: 14, marginBottom: 8 }} />
      <div style={{ ...bar, width: '90%', height: 12, marginBottom: 6 }} />
      <div style={{ ...bar, width: '60%', height: 12 }} />
    </div>
  );
};

const InlineSkeleton: React.FC = () => (
  <div style={{ padding: 16 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ marginBottom: 16, padding: 16, borderRadius: 12, background: '#f8fafc' }}>
        <div style={{ ...bar, width: '50%', height: 16, marginBottom: 8 }} />
        <div style={{ ...bar, width: '100%', height: 12, marginBottom: 6 }} />
        <div style={{ ...bar, width: '85%', height: 12 }} />
      </div>
    ))}
  </div>
);

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(15, 23, 42, 0.45)',
  animation: backdropAnim(),
  padding: 16,
};

const modalCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: '#ffffff',
  borderRadius: 16,
  padding: '28px 28px 32px',
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.18)',
  animation: modalAnim(),
};

const drawerBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.4)',
  animation: backdropAnim(),
};

const drawerPanel: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 'min(100%, 420px)',
  background: '#ffffff',
  padding: '32px 28px',
  boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.12)',
};

const bannerBase: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  height: 56,
  background: '#ffffff',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
};

const toastBase: React.CSSProperties = {
  position: 'fixed',
  width: 320,
  background: '#ffffff',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
  animation: modalAnim(),
};

const bar: React.CSSProperties = {
  background: '#e5e7eb',
  borderRadius: 6,
  animation: 'chirp-skeleton 1.4s ease-in-out infinite',
};
